import React, { useState, useEffect, useRef } from "react";
import { api } from "../api";
import toast from "../components/Toast";

const CATEGORY_COLORS = {
  "Career & Applications": "#06b6d4",
  "Business & Legal": "#a855f7",
  "Academic & Institutional": "#f59e0b",
  Financial: "#10b981",
  Medical: "#ef4444",
  "Real Estate": "#3b82f6",
  Personal: "#ec4899",
  Operations: "#f97316",
};

const CATEGORIES = [
  "All",
  "Career & Applications",
  "Business & Legal",
  "Academic & Institutional",
  "Financial",
  "Medical",
  "Real Estate",
  "Personal",
  "Operations",
];

const CATEGORY_ICONS = {
  All: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
    </svg>
  ),
  "Career & Applications": (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
    </svg>
  ),
  "Business & Legal": (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v4M12 14v4M16 14v4" />
    </svg>
  ),
  "Academic & Institutional": (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5" />
    </svg>
  ),
  Financial: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  Medical: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  "Real Estate": (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  Personal: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Operations: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
};

const TEMPLATES = [
  {
    id: "cv-resume",
    label: "Professional CV/Resume",
    category: "Career & Applications",
    description: "ATS-optimized resume with professional formatting",
    fields: [
      { key: "full_name", label: "Full Name", type: "text", placeholder: "John Smith", required: true },
      { key: "title", label: "Professional Title", type: "text", placeholder: "Senior Software Engineer" },
      { key: "experience", label: "Work Experience Summary", type: "textarea", placeholder: "List your key roles, companies, and achievements" },
      { key: "skills", label: "Key Skills", type: "textarea", placeholder: "JavaScript, React, Node.js, Project Management" },
      { key: "education", label: "Education", type: "textarea", placeholder: "BSc Computer Science, MIT, 2018" },
      { key: "target_role", label: "Target Role/Industry", type: "text", placeholder: "Senior Frontend Developer at FAANG" },
    ],
    buildPrompt: (f) => `Create a professional ATS-optimized CV/Resume for ${f.full_name || "the candidate"}. Professional title: ${f.title || "not specified"}. Experience: ${f.experience || "to be detailed"}. Skills: ${f.skills || "various"}. Education: ${f.education || "to be specified"}. Target role: ${f.target_role || "open to opportunities"}. Include proper section headings, bullet points for achievements with metrics, and professional formatting. Include signature placement area at the bottom.`,
  },
  {
    id: "cover-letter",
    label: "Cover Letter",
    category: "Career & Applications",
    description: "Tailored cover letter for job applications",
    fields: [
      { key: "full_name", label: "Your Name", type: "text", placeholder: "John Smith", required: true },
      { key: "company", label: "Company Name", type: "text", placeholder: "Google", required: true },
      { key: "position", label: "Position Applied For", type: "text", placeholder: "Senior Product Manager", required: true },
      { key: "experience", label: "Relevant Experience", type: "textarea", placeholder: "5 years in product management, launched 3 SaaS products" },
      { key: "why_company", label: "Why This Company", type: "textarea", placeholder: "Passion for innovation, company culture alignment" },
    ],
    buildPrompt: (f) => `Write a compelling cover letter from ${f.full_name || "the applicant"} to ${f.company} for the ${f.position} position. Relevant experience: ${f.experience || "professional background"}. Why this company: ${f.why_company || "genuine interest"}. Include proper letterhead, official formatting, signature placement area, and professional tone. Format as a formal business letter.`,
  },
  {
    id: "motivational-letter",
    label: "Motivational Letter",
    category: "Career & Applications",
    description: "Personal statement for academic or career applications",
    fields: [
      { key: "full_name", label: "Your Name", type: "text", placeholder: "John Smith", required: true },
      { key: "purpose", label: "Purpose", type: "select", options: ["University Admission", "Scholarship", "Fellowship", "Internship", "Visa Application"], required: true },
      { key: "institution", label: "Institution/Organization", type: "text", placeholder: "Harvard University" },
      { key: "background", label: "Your Background", type: "textarea", placeholder: "Academic achievements, relevant experiences" },
      { key: "goals", label: "Goals & Motivation", type: "textarea", placeholder: "Career goals, why this opportunity matters" },
    ],
    buildPrompt: (f) => `Write a motivational letter for ${f.full_name || "the applicant"} for ${f.purpose || "application"} at ${f.institution || "the institution"}. Background: ${f.background || "to be detailed"}. Goals: ${f.goals || "professional development"}. Include proper formatting, compelling narrative, and signature placement area.`,
  },
  {
    id: "english-proficiency",
    label: "English Proficiency Certificate",
    category: "Career & Applications",
    description: "Official language proficiency certification document",
    fields: [
      { key: "full_name", label: "Candidate Name", type: "text", placeholder: "John Smith", required: true },
      { key: "institution", label: "Issuing Institution", type: "text", placeholder: "International Language Center" },
      { key: "level", label: "Proficiency Level", type: "select", options: ["A1 (Beginner)", "A2 (Elementary)", "B1 (Intermediate)", "B2 (Upper Intermediate)", "C1 (Advanced)", "C2 (Proficient)"] },
      { key: "date", label: "Issue Date", type: "text", placeholder: "June 2026" },
    ],
    buildPrompt: (f) => `Create an official English Proficiency Certificate for ${f.full_name || "the candidate"} issued by ${f.institution || "the Language Institute"}. Level: ${f.level || "B2"}. Date: ${f.date || "current date"}. Include proper letterhead, official formatting, certificate number, signature placement area, stamp area, and institutional seal description.`,
  },
  {
    id: "recommendation-letter",
    label: "Letter of Recommendation",
    category: "Career & Applications",
    description: "Professional or academic reference letter",
    fields: [
      { key: "candidate", label: "Candidate Name", type: "text", placeholder: "John Smith", required: true },
      { key: "recommender", label: "Recommender Name & Title", type: "text", placeholder: "Dr. Sarah Wilson, Department Head" },
      { key: "relationship", label: "Professional Relationship", type: "text", placeholder: "Direct supervisor for 3 years" },
      { key: "strengths", label: "Key Strengths to Highlight", type: "textarea", placeholder: "Leadership, technical expertise, problem-solving" },
      { key: "purpose", label: "Purpose of Recommendation", type: "text", placeholder: "Graduate school admission, job application" },
    ],
    buildPrompt: (f) => `Write a strong letter of recommendation for ${f.candidate || "the candidate"} from ${f.recommender || "the recommender"}. Relationship: ${f.relationship || "professional colleague"}. Strengths: ${f.strengths || "exceptional professional qualities"}. Purpose: ${f.purpose || "career advancement"}. Include proper letterhead, official formatting, signature placement area, and stamp area.`,
  },
  {
    id: "statement-of-purpose",
    label: "Statement of Purpose",
    category: "Career & Applications",
    description: "Academic SOP for graduate admissions",
    fields: [
      { key: "full_name", label: "Your Name", type: "text", placeholder: "John Smith", required: true },
      { key: "program", label: "Program/University", type: "text", placeholder: "MS Computer Science, Stanford", required: true },
      { key: "background", label: "Academic Background", type: "textarea", placeholder: "BSc CS, research experience, publications" },
      { key: "research_interests", label: "Research Interests", type: "textarea", placeholder: "Machine learning, NLP, computer vision" },
      { key: "career_goals", label: "Career Goals", type: "textarea", placeholder: "Research scientist, professor, industry leader" },
    ],
    buildPrompt: (f) => `Write a compelling Statement of Purpose for ${f.full_name || "the applicant"} applying to ${f.program || "the graduate program"}. Background: ${f.background || "strong academic record"}. Research interests: ${f.research_interests || "to be specified"}. Career goals: ${f.career_goals || "academic and professional growth"}. Format as a formal academic document with clear paragraphs.`,
  },
  {
    id: "resignation-letter",
    label: "Resignation Letter",
    category: "Career & Applications",
    description: "Professional resignation with proper notice",
    fields: [
      { key: "full_name", label: "Your Name", type: "text", placeholder: "John Smith", required: true },
      { key: "company", label: "Company Name", type: "text", placeholder: "Current employer" },
      { key: "position", label: "Current Position", type: "text", placeholder: "Software Engineer" },
      { key: "last_day", label: "Last Working Day", type: "text", placeholder: "July 15, 2026" },
      { key: "reason", label: "Reason (optional)", type: "textarea", placeholder: "Career growth opportunity, relocation" },
    ],
    buildPrompt: (f) => `Write a professional resignation letter from ${f.full_name || "the employee"} to ${f.company || "the company"}, position: ${f.position || "current role"}. Last working day: ${f.last_day || "two weeks from now"}. Reason: ${f.reason || "personal career growth"}. Include proper letterhead, formal tone, gratitude, and signature placement area.`,
  },
  {
    id: "job-offer-acceptance",
    label: "Job Offer Acceptance Letter",
    category: "Career & Applications",
    description: "Formal acceptance of employment offer",
    fields: [
      { key: "full_name", label: "Your Name", type: "text", placeholder: "John Smith", required: true },
      { key: "company", label: "Company Name", type: "text", placeholder: "New employer", required: true },
      { key: "position", label: "Position Offered", type: "text", placeholder: "Senior Developer" },
      { key: "start_date", label: "Start Date", type: "text", placeholder: "August 1, 2026" },
      { key: "salary", label: "Agreed Salary", type: "text", placeholder: "$120,000/year" },
    ],
    buildPrompt: (f) => `Write a formal job offer acceptance letter from ${f.full_name || "the candidate"} accepting the ${f.position || "offered position"} at ${f.company}. Start date: ${f.start_date || "as agreed"}. Salary: ${f.salary || "as discussed"}. Include formal letterhead formatting and signature placement area.`,
  },
  {
    id: "interview-followup",
    label: "Interview Thank You/Follow-up",
    category: "Career & Applications",
    description: "Post-interview thank you email",
    fields: [
      { key: "interviewer", label: "Interviewer Name", type: "text", placeholder: "Sarah Johnson", required: true },
      { key: "company", label: "Company", type: "text", placeholder: "Google" },
      { key: "position", label: "Position", type: "text", placeholder: "Product Manager" },
      { key: "highlights", label: "Interview Highlights", type: "textarea", placeholder: "Discussed team culture, product roadmap" },
    ],
    buildPrompt: (f) => `Write a professional interview thank you/follow-up email to ${f.interviewer || "the interviewer"} at ${f.company || "the company"} for the ${f.position || "position"} role. Highlights from interview: ${f.highlights || "engaging conversation about the role"}. Keep professional but personable.`,
  },
  {
    id: "linkedin-summary",
    label: "LinkedIn Profile Summary",
    category: "Career & Applications",
    description: "Optimized LinkedIn About section",
    fields: [
      { key: "full_name", label: "Your Name", type: "text", placeholder: "John Smith", required: true },
      { key: "title", label: "Professional Title", type: "text", placeholder: "Full Stack Developer" },
      { key: "experience_years", label: "Years of Experience", type: "text", placeholder: "8" },
      { key: "specialties", label: "Specialties", type: "textarea", placeholder: "React, Node.js, AWS, Team Leadership" },
      { key: "achievements", label: "Key Achievements", type: "textarea", placeholder: "Led team of 12, increased revenue 40%" },
    ],
    buildPrompt: (f) => `Write an optimized LinkedIn profile summary for ${f.full_name || "the professional"}. Title: ${f.title || "professional"}. ${f.experience_years || "several"} years experience. Specialties: ${f.specialties || "various"}. Achievements: ${f.achievements || "significant impact"}. Make it engaging, keyword-rich, and within 2600 characters.`,
  },
  {
    id: "employment-verification",
    label: "Employment Verification Letter",
    category: "Career & Applications",
    description: "Official employment confirmation document",
    fields: [
      { key: "employee_name", label: "Employee Name", type: "text", placeholder: "John Smith", required: true },
      { key: "company", label: "Company Name", type: "text", placeholder: "Tech Corp", required: true },
      { key: "position", label: "Position/Title", type: "text", placeholder: "Senior Engineer" },
      { key: "start_date", label: "Employment Start Date", type: "text", placeholder: "January 2020" },
      { key: "salary", label: "Current Salary (optional)", type: "text", placeholder: "$95,000/year" },
    ],
    buildPrompt: (f) => `Create an official employment verification letter for ${f.employee_name || "the employee"} at ${f.company}. Position: ${f.position || "as employed"}. Start date: ${f.start_date || "date of joining"}. Salary: ${f.salary || "as per records"}. Include proper company letterhead, official formatting, HR signature placement area, stamp area, and company seal description.`,
  },
  {
    id: "business-plan",
    label: "Business Plan",
    category: "Business & Legal",
    description: "Comprehensive business plan with projections",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "TechStart Solutions", required: true },
      { key: "industry", label: "Industry", type: "text", placeholder: "SaaS, E-commerce, Healthcare" },
      { key: "stage", label: "Stage", type: "select", options: ["Idea", "Pre-seed", "Seed", "Series A", "Growth"] },
      { key: "target_market", label: "Target Market", type: "textarea", placeholder: "SMBs in North America" },
      { key: "revenue_model", label: "Revenue Model", type: "text", placeholder: "Subscription, Freemium, Marketplace" },
    ],
    buildPrompt: (f) => `Create a comprehensive business plan for ${f.company || "the company"}. Industry: ${f.industry || "technology"}. Stage: ${f.stage || "early stage"}. Target market: ${f.target_market || "to be defined"}. Revenue model: ${f.revenue_model || "to be determined"}. Include executive summary, market analysis, revenue model, competitive landscape, team structure, and 3-year financial projections.`,
  },
  {
    id: "pitch-deck-script",
    label: "Pitch Deck Script",
    category: "Business & Legal",
    description: "10-slide investor pitch deck narrative",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "TechStart", required: true },
      { key: "problem", label: "Problem Statement", type: "textarea", placeholder: "What pain point do you address?" },
      { key: "solution", label: "Your Solution", type: "textarea", placeholder: "How does your product solve it?" },
      { key: "funding", label: "Funding Ask", type: "text", placeholder: "$500K, $2M" },
      { key: "traction", label: "Current Traction", type: "textarea", placeholder: "Users, revenue, partnerships" },
    ],
    buildPrompt: (f) => `Write a compelling 10-slide investor pitch deck script for ${f.company}. Problem: ${f.problem || "market gap"}. Solution: ${f.solution || "innovative approach"}. Funding ask: ${f.funding || "seed round"}. Traction: ${f.traction || "early momentum"}. Include slides for: problem, solution, market size (TAM/SAM/SOM), business model, traction, competition, team, financials, and the ask.`,
  },
  {
    id: "nda",
    label: "NDA",
    category: "Business & Legal",
    description: "Non-Disclosure Agreement template",
    fields: [
      { key: "discloser", label: "Disclosing Party", type: "text", placeholder: "Company A", required: true },
      { key: "recipient", label: "Receiving Party", type: "text", placeholder: "Company B or Individual", required: true },
      { key: "purpose", label: "Purpose of Disclosure", type: "textarea", placeholder: "Business partnership evaluation" },
      { key: "duration", label: "NDA Duration", type: "select", options: ["1 year", "2 years", "3 years", "5 years", "Indefinite"] },
      { key: "jurisdiction", label: "Jurisdiction", type: "text", placeholder: "State of Delaware, USA" },
    ],
    buildPrompt: (f) => `Draft a professional Non-Disclosure Agreement between ${f.discloser} (Disclosing Party) and ${f.recipient} (Receiving Party). Purpose: ${f.purpose || "business discussions"}. Duration: ${f.duration || "2 years"}. Jurisdiction: ${f.jurisdiction || "to be specified"}. Include definitions, obligations, exclusions, remedies, signature blocks, and stamp area. Note: template for review by legal counsel.`,
  },
  {
    id: "invoice-quotation",
    label: "Invoice/Quotation",
    category: "Business & Legal",
    description: "Professional invoice or price quotation",
    fields: [
      { key: "from_company", label: "From (Your Company)", type: "text", placeholder: "Your Business Name", required: true },
      { key: "to_company", label: "To (Client)", type: "text", placeholder: "Client Company Name", required: true },
      { key: "items", label: "Items/Services", type: "textarea", placeholder: "Web Development - $5000\nUI Design - $2000", required: true },
      { key: "type", label: "Document Type", type: "select", options: ["Invoice", "Quotation", "Estimate"] },
      { key: "payment_terms", label: "Payment Terms", type: "text", placeholder: "Net 30, 50% upfront" },
    ],
    buildPrompt: (f) => `Create a professional ${f.type || "Invoice"} from ${f.from_company} to ${f.to_company}. Items: ${f.items || "services rendered"}. Payment terms: ${f.payment_terms || "Net 30"}. Include proper letterhead, invoice number placeholder, itemized table, subtotal, tax, total, payment instructions, and company stamp area.`,
  },
  {
    id: "service-agreement",
    label: "Service Agreement/Contract",
    category: "Business & Legal",
    description: "Comprehensive service contract",
    fields: [
      { key: "provider", label: "Service Provider", type: "text", placeholder: "Your Company", required: true },
      { key: "client", label: "Client", type: "text", placeholder: "Client Company", required: true },
      { key: "services", label: "Services Described", type: "textarea", placeholder: "Web development, maintenance, support" },
      { key: "duration", label: "Contract Duration", type: "text", placeholder: "12 months" },
      { key: "value", label: "Contract Value", type: "text", placeholder: "$50,000" },
    ],
    buildPrompt: (f) => `Draft a comprehensive Service Agreement between ${f.provider} (Provider) and ${f.client} (Client). Services: ${f.services || "professional services"}. Duration: ${f.duration || "to be agreed"}. Value: ${f.value || "as quoted"}. Include scope, deliverables, payment schedule, warranties, liability, termination, IP rights, signature blocks, and stamp areas.`,
  },
  {
    id: "company-profile",
    label: "Company Profile",
    category: "Business & Legal",
    description: "Corporate profile document",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Your Company", required: true },
      { key: "industry", label: "Industry", type: "text", placeholder: "Technology, Healthcare, Finance" },
      { key: "founded", label: "Year Founded", type: "text", placeholder: "2020" },
      { key: "team_size", label: "Team Size", type: "text", placeholder: "50 employees" },
      { key: "achievements", label: "Key Achievements", type: "textarea", placeholder: "Awards, milestones, notable clients" },
    ],
    buildPrompt: (f) => `Create a professional company profile document for ${f.company}. Industry: ${f.industry || "technology"}. Founded: ${f.founded || "recent years"}. Team: ${f.team_size || "growing team"}. Achievements: ${f.achievements || "various milestones"}. Include company overview, mission/vision, services, team highlights, client logos section, and contact information.`,
  },
  {
    id: "employee-offer-letter",
    label: "Employee Offer Letter",
    category: "Business & Legal",
    description: "Formal employment offer document",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Your Company", required: true },
      { key: "candidate", label: "Candidate Name", type: "text", placeholder: "John Smith", required: true },
      { key: "position", label: "Position Offered", type: "text", placeholder: "Software Engineer" },
      { key: "salary", label: "Salary/Compensation", type: "text", placeholder: "$100,000/year + benefits" },
      { key: "start_date", label: "Start Date", type: "text", placeholder: "August 1, 2026" },
    ],
    buildPrompt: (f) => `Create a formal employee offer letter from ${f.company} to ${f.candidate} for the position of ${f.position || "offered role"}. Compensation: ${f.salary || "competitive package"}. Start date: ${f.start_date || "to be confirmed"}. Include company letterhead, position details, compensation breakdown, benefits, terms of employment, signature areas for both parties, and company stamp area.`,
  },
  {
    id: "partnership-agreement",
    label: "Partnership Agreement",
    category: "Business & Legal",
    description: "Business partnership contract",
    fields: [
      { key: "partner_a", label: "Partner A", type: "text", placeholder: "First Partner/Company", required: true },
      { key: "partner_b", label: "Partner B", type: "text", placeholder: "Second Partner/Company", required: true },
      { key: "business_type", label: "Business Type", type: "text", placeholder: "Technology consulting" },
      { key: "split", label: "Profit/Equity Split", type: "text", placeholder: "50/50, 60/40" },
      { key: "jurisdiction", label: "Jurisdiction", type: "text", placeholder: "State of California" },
    ],
    buildPrompt: (f) => `Draft a Partnership Agreement between ${f.partner_a} and ${f.partner_b}. Business: ${f.business_type || "joint venture"}. Split: ${f.split || "to be agreed"}. Jurisdiction: ${f.jurisdiction || "to be specified"}. Include purpose, capital contributions, profit sharing, management duties, decision-making, dissolution terms, signature blocks, and notary stamp area.`,
  },
  {
    id: "non-compete",
    label: "Non-Compete/Non-Solicitation",
    category: "Business & Legal",
    description: "Non-compete and non-solicitation agreement",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Employer Company", required: true },
      { key: "employee", label: "Employee/Contractor Name", type: "text", placeholder: "John Smith", required: true },
      { key: "duration", label: "Restriction Period", type: "select", options: ["6 months", "1 year", "2 years", "3 years"] },
      { key: "geography", label: "Geographic Scope", type: "text", placeholder: "United States, 50-mile radius" },
      { key: "industry", label: "Industry Restriction", type: "text", placeholder: "SaaS, cloud computing" },
    ],
    buildPrompt: (f) => `Draft a Non-Compete and Non-Solicitation Agreement between ${f.company} and ${f.employee}. Duration: ${f.duration || "1 year"}. Geographic scope: ${f.geography || "reasonable area"}. Industry: ${f.industry || "same industry"}. Include definitions, restrictions, consideration, remedies, severability, signature blocks, and witness/notary areas.`,
  },
  {
    id: "terms-privacy",
    label: "Terms of Service & Privacy Policy",
    category: "Business & Legal",
    description: "Website/app legal documents",
    fields: [
      { key: "company", label: "Company/App Name", type: "text", placeholder: "Your Company", required: true },
      { key: "website", label: "Website URL", type: "url", placeholder: "https://yoursite.com" },
      { key: "service_type", label: "Type of Service", type: "text", placeholder: "SaaS platform, mobile app, marketplace" },
      { key: "data_collected", label: "Data Collected", type: "textarea", placeholder: "Email, name, payment info, usage data" },
      { key: "jurisdiction", label: "Jurisdiction", type: "text", placeholder: "United States" },
    ],
    buildPrompt: (f) => `Create comprehensive Terms of Service and Privacy Policy for ${f.company}${f.website ? ` (${f.website})` : ""}. Service type: ${f.service_type || "online platform"}. Data collected: ${f.data_collected || "standard user data"}. Jurisdiction: ${f.jurisdiction || "United States"}. Include GDPR and CCPA compliance sections, user rights, data retention, cookies policy, and acceptable use.`,
  },
  {
    id: "press-release",
    label: "Press Release",
    category: "Business & Legal",
    description: "Professional media announcement",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Your Company", required: true },
      { key: "announcement", label: "Announcement", type: "textarea", placeholder: "Product launch, funding round, partnership", required: true },
      { key: "quote_person", label: "Quote From (Name & Title)", type: "text", placeholder: "CEO John Smith" },
      { key: "date", label: "Release Date", type: "text", placeholder: "June 23, 2026" },
    ],
    buildPrompt: (f) => `Write a professional press release for ${f.company}. Announcement: ${f.announcement}. Quote from: ${f.quote_person || "company leadership"}. Date: ${f.date || "immediate release"}. Follow AP style, include headline, subhead, dateline, body with quotes, boilerplate, and media contact info section.`,
  },
  {
    id: "competitor-analysis",
    label: "Competitor Analysis",
    category: "Business & Legal",
    description: "SWOT and competitive landscape report",
    fields: [
      { key: "company", label: "Your Company", type: "text", placeholder: "Your company name", required: true },
      { key: "competitors", label: "Competitors", type: "textarea", placeholder: "Competitor A, Competitor B, Competitor C" },
      { key: "industry", label: "Industry/Market", type: "text", placeholder: "Food delivery, CRM software" },
    ],
    buildPrompt: (f) => `Create a detailed SWOT analysis and competitive landscape report for ${f.company} in ${f.industry || "the market"}. Competitors: ${f.competitors || "top 3-5 competitors"}. Include pricing comparison, feature matrix, market positioning, strengths/weaknesses, and strategic recommendations.`,
  },
  {
    id: "market-research",
    label: "Market Research",
    category: "Business & Legal",
    description: "TAM/SAM/SOM analysis with segments",
    fields: [
      { key: "market", label: "Market/Industry", type: "text", placeholder: "AI-powered customer service", required: true },
      { key: "region", label: "Geographic Focus", type: "text", placeholder: "North America, Global" },
      { key: "segments", label: "Target Segments", type: "textarea", placeholder: "SMBs, Enterprise, Startups" },
    ],
    buildPrompt: (f) => `Conduct comprehensive market research for the ${f.market} market${f.region ? ` in ${f.region}` : ""}. Segments: ${f.segments || "all segments"}. Include TAM/SAM/SOM with numbers, customer personas, growth trends, market opportunities, and entry barriers.`,
  },
  {
    id: "marketing-strategy",
    label: "Marketing Strategy",
    category: "Business & Legal",
    description: "90-day integrated marketing plan",
    fields: [
      { key: "company", label: "Company/Product", type: "text", placeholder: "Company name", required: true },
      { key: "budget", label: "Monthly Budget", type: "text", placeholder: "$5,000 - $50,000" },
      { key: "goals", label: "Primary Goals", type: "textarea", placeholder: "Increase signups 50%, generate 100 leads/month" },
      { key: "channels", label: "Preferred Channels", type: "textarea", placeholder: "Social media, SEO, paid ads, email" },
    ],
    buildPrompt: (f) => `Create a 90-day integrated marketing strategy for ${f.company}. Budget: ${f.budget || "flexible"}. Goals: ${f.goals || "growth and awareness"}. Channels: ${f.channels || "multi-channel approach"}. Include channel strategy, budget allocation, KPIs, campaign calendar, and expected ROI.`,
  },
  {
    id: "email-campaign",
    label: "Email Campaign",
    category: "Business & Legal",
    description: "7-email nurture sequence",
    fields: [
      { key: "product", label: "Product/Service", type: "text", placeholder: "What are you selling?", required: true },
      { key: "audience", label: "Target Audience", type: "text", placeholder: "SaaS founders, HR managers" },
      { key: "goal", label: "Campaign Goal", type: "select", options: ["Onboarding", "Sales Conversion", "Re-engagement", "Upsell", "Educational"] },
    ],
    buildPrompt: (f) => `Write a high-converting 7-email ${f.goal || "nurture"} sequence for ${f.product}. Audience: ${f.audience || "potential customers"}. Include subject lines, preview text, email body, and CTAs for each email.`,
  },
  {
    id: "content-calendar",
    label: "Content Calendar",
    category: "Business & Legal",
    description: "4-week social media content plan",
    fields: [
      { key: "brand", label: "Brand Name", type: "text", placeholder: "Your brand", required: true },
      { key: "platforms", label: "Platforms", type: "text", placeholder: "Instagram, LinkedIn, Twitter" },
      { key: "tone", label: "Brand Voice", type: "select", options: ["Professional", "Casual", "Educational", "Inspirational", "Bold"] },
      { key: "topics", label: "Key Topics", type: "textarea", placeholder: "Product tips, industry news, behind the scenes" },
    ],
    buildPrompt: (f) => `Create a 4-week social media content calendar for ${f.brand} on ${f.platforms || "major platforms"}. Voice: ${f.tone || "professional"}. Topics: ${f.topics || "engaging variety"}. Include ready-to-post copy, hashtags, optimal times, and engagement hooks.`,
  },
  {
    id: "seo-strategy",
    label: "SEO Strategy",
    category: "Business & Legal",
    description: "Keyword research and content pillars",
    fields: [
      { key: "website", label: "Website URL", type: "url", placeholder: "https://yoursite.com", required: true },
      { key: "keywords", label: "Target Keywords", type: "textarea", placeholder: "best CRM software, sales automation" },
      { key: "competitors", label: "SEO Competitors", type: "text", placeholder: "hubspot.com, salesforce.com" },
    ],
    buildPrompt: (f) => `Develop an SEO strategy for ${f.website}. Keywords: ${f.keywords || "to be researched"}. Competitors: ${f.competitors || "top ranking sites"}. Include keyword research, content pillars, on-page optimization, technical SEO recommendations, and link building strategy.`,
  },
  {
    id: "research-proposal",
    label: "Research Proposal",
    category: "Academic & Institutional",
    description: "Academic research proposal document",
    fields: [
      { key: "title", label: "Research Title", type: "text", placeholder: "Impact of AI on Healthcare Outcomes", required: true },
      { key: "researcher", label: "Researcher Name", type: "text", placeholder: "Dr. John Smith" },
      { key: "institution", label: "Institution", type: "text", placeholder: "MIT, Stanford University" },
      { key: "field", label: "Field of Study", type: "text", placeholder: "Computer Science, Medicine" },
      { key: "methodology", label: "Proposed Methodology", type: "textarea", placeholder: "Quantitative study, surveys, experiments" },
    ],
    buildPrompt: (f) => `Write a formal academic research proposal titled "${f.title}" by ${f.researcher || "the researcher"} at ${f.institution || "the institution"}. Field: ${f.field || "academic discipline"}. Methodology: ${f.methodology || "to be detailed"}. Include abstract, introduction, literature review, methodology, timeline, budget, expected outcomes, and references section.`,
  },
  {
    id: "thesis-abstract",
    label: "Thesis Abstract",
    category: "Academic & Institutional",
    description: "Thesis/dissertation abstract",
    fields: [
      { key: "title", label: "Thesis Title", type: "text", placeholder: "Your thesis title", required: true },
      { key: "field", label: "Field/Department", type: "text", placeholder: "Computer Science" },
      { key: "methodology", label: "Methodology Used", type: "textarea", placeholder: "Mixed methods, quantitative analysis" },
      { key: "findings", label: "Key Findings", type: "textarea", placeholder: "Main results and contributions" },
      { key: "word_limit", label: "Word Limit", type: "select", options: ["150 words", "250 words", "350 words", "500 words"] },
    ],
    buildPrompt: (f) => `Write a thesis abstract for "${f.title}" in ${f.field || "the academic field"}. Methodology: ${f.methodology || "research methods"}. Findings: ${f.findings || "key results"}. Limit: ${f.word_limit || "250 words"}. Include background, objective, methods, results, and conclusion in structured format.`,
  },
  {
    id: "academic-reference",
    label: "Academic Reference Letter",
    category: "Academic & Institutional",
    description: "Faculty reference for student",
    fields: [
      { key: "student", label: "Student Name", type: "text", placeholder: "Student full name", required: true },
      { key: "professor", label: "Professor Name & Title", type: "text", placeholder: "Prof. Sarah Wilson, PhD" },
      { key: "course", label: "Course/Program", type: "text", placeholder: "Advanced Machine Learning" },
      { key: "qualities", label: "Student Qualities", type: "textarea", placeholder: "Exceptional analytical skills, research aptitude" },
      { key: "purpose", label: "Reference Purpose", type: "select", options: ["Graduate Admission", "PhD Program", "Scholarship", "Research Position", "Fellowship"] },
    ],
    buildPrompt: (f) => `Write an academic reference letter for ${f.student} from ${f.professor || "the faculty member"}. Course: ${f.course || "relevant coursework"}. Qualities: ${f.qualities || "strong academic performance"}. Purpose: ${f.purpose || "academic advancement"}. Include university letterhead formatting, specific examples, academic assessment, and signature placement area.`,
  },
  {
    id: "course-certificate",
    label: "Course Completion Certificate",
    category: "Academic & Institutional",
    description: "Training/course completion document",
    fields: [
      { key: "recipient", label: "Recipient Name", type: "text", placeholder: "Student name", required: true },
      { key: "course", label: "Course/Program Name", type: "text", placeholder: "Advanced Data Science", required: true },
      { key: "institution", label: "Institution/Organization", type: "text", placeholder: "Tech Academy" },
      { key: "duration", label: "Course Duration", type: "text", placeholder: "12 weeks, 120 hours" },
      { key: "date", label: "Completion Date", type: "text", placeholder: "June 2026" },
    ],
    buildPrompt: (f) => `Create a Course Completion Certificate for ${f.recipient} who completed "${f.course}" at ${f.institution || "the institution"}. Duration: ${f.duration || "as specified"}. Date: ${f.date || "current"}. Include official certificate layout, certificate number, institutional seal description, authorized signature areas, and stamp placement.`,
  },
  {
    id: "scholarship-recommendation",
    label: "Scholarship Recommendation Letter",
    category: "Academic & Institutional",
    description: "Recommendation for scholarship application",
    fields: [
      { key: "student", label: "Student Name", type: "text", placeholder: "Student full name", required: true },
      { key: "scholarship", label: "Scholarship Name", type: "text", placeholder: "Fulbright, Rhodes, Merit" },
      { key: "recommender", label: "Recommender", type: "text", placeholder: "Dr. Wilson, Department Chair" },
      { key: "achievements", label: "Student Achievements", type: "textarea", placeholder: "GPA, research, community service" },
    ],
    buildPrompt: (f) => `Write a scholarship recommendation letter for ${f.student} applying to ${f.scholarship || "the scholarship"} from ${f.recommender || "academic advisor"}. Achievements: ${f.achievements || "outstanding academic record"}. Include institutional letterhead, specific examples, comparison to peers, signature placement, and official stamp area.`,
  },
  {
    id: "grant-proposal",
    label: "Grant Proposal",
    category: "Academic & Institutional",
    description: "Research/project grant application",
    fields: [
      { key: "project_title", label: "Project Title", type: "text", placeholder: "Project name", required: true },
      { key: "organization", label: "Applying Organization", type: "text", placeholder: "Your institution" },
      { key: "grant_body", label: "Grant Body", type: "text", placeholder: "NSF, NIH, EU Horizon" },
      { key: "amount", label: "Amount Requested", type: "text", placeholder: "$500,000" },
      { key: "duration", label: "Project Duration", type: "text", placeholder: "3 years" },
    ],
    buildPrompt: (f) => `Write a grant proposal for "${f.project_title}" from ${f.organization || "the institution"} to ${f.grant_body || "the funding body"}. Amount: ${f.amount || "to be specified"}. Duration: ${f.duration || "multi-year"}. Include executive summary, problem statement, objectives, methodology, budget justification, timeline, expected impact, and evaluation plan.`,
  },
  {
    id: "visa-support-letter",
    label: "Visa/Immigration Support Letter",
    category: "Academic & Institutional",
    description: "Institutional support for visa application",
    fields: [
      { key: "applicant", label: "Applicant Name", type: "text", placeholder: "Visa applicant name", required: true },
      { key: "institution", label: "Sponsoring Institution", type: "text", placeholder: "University or Company name", required: true },
      { key: "purpose", label: "Purpose of Visit", type: "select", options: ["Study", "Research", "Conference", "Employment", "Training"] },
      { key: "duration", label: "Duration of Stay", type: "text", placeholder: "2 years, 6 months" },
      { key: "country", label: "Destination Country", type: "text", placeholder: "United States, UK, Canada" },
    ],
    buildPrompt: (f) => `Create a visa/immigration support letter for ${f.applicant} from ${f.institution}. Purpose: ${f.purpose || "academic"}. Duration: ${f.duration || "as required"}. Country: ${f.country || "destination"}. Include official letterhead, applicant details, institutional commitment, financial support details, contact information, authorized signature area, and official stamp/seal area.`,
  },
  {
    id: "course-syllabus",
    label: "Course Syllabus/Outline",
    category: "Academic & Institutional",
    description: "Academic course structure document",
    fields: [
      { key: "course_name", label: "Course Name", type: "text", placeholder: "Introduction to Machine Learning", required: true },
      { key: "instructor", label: "Instructor", type: "text", placeholder: "Prof. Smith" },
      { key: "level", label: "Level", type: "select", options: ["Undergraduate", "Graduate", "Professional", "Beginner", "Advanced"] },
      { key: "duration", label: "Duration", type: "text", placeholder: "16 weeks, 1 semester" },
      { key: "topics", label: "Key Topics", type: "textarea", placeholder: "Regression, classification, neural networks" },
    ],
    buildPrompt: (f) => `Create a course syllabus for "${f.course_name}" taught by ${f.instructor || "the instructor"}. Level: ${f.level || "undergraduate"}. Duration: ${f.duration || "one semester"}. Topics: ${f.topics || "core curriculum"}. Include course description, learning objectives, weekly schedule, assignments, grading policy, required materials, and academic integrity policy.`,
  },
  {
    id: "degree-certificate",
    label: "Degree/Diploma Certificate",
    category: "Academic & Institutional",
    description: "Academic degree certification document",
    fields: [
      { key: "recipient", label: "Graduate Name", type: "text", placeholder: "Full legal name", required: true },
      { key: "degree", label: "Degree", type: "text", placeholder: "Bachelor of Science in Computer Science", required: true },
      { key: "institution", label: "University/Institution", type: "text", placeholder: "University name" },
      { key: "date", label: "Conferral Date", type: "text", placeholder: "May 2026" },
      { key: "honors", label: "Honors (optional)", type: "select", options: ["None", "Cum Laude", "Magna Cum Laude", "Summa Cum Laude", "With Distinction", "First Class"] },
    ],
    buildPrompt: (f) => `Create a formal degree/diploma certificate for ${f.recipient} who earned a ${f.degree} from ${f.institution || "the university"}. Date: ${f.date || "current year"}. Honors: ${f.honors || "none"}. Include ornate certificate layout description, university seal, registrar signature area, dean signature area, certificate number, and embossed stamp description.`,
  },
  {
    id: "lesson-plan",
    label: "Lesson Plan",
    category: "Academic & Institutional",
    description: "Structured teaching lesson plan",
    fields: [
      { key: "subject", label: "Subject/Topic", type: "text", placeholder: "Photosynthesis, World War II", required: true },
      { key: "grade_level", label: "Grade/Level", type: "text", placeholder: "Grade 10, University Year 2" },
      { key: "duration", label: "Lesson Duration", type: "select", options: ["30 minutes", "45 minutes", "60 minutes", "90 minutes", "2 hours"] },
      { key: "objectives", label: "Learning Objectives", type: "textarea", placeholder: "Students will be able to..." },
    ],
    buildPrompt: (f) => `Create a detailed lesson plan for teaching ${f.subject}. Grade: ${f.grade_level || "secondary"}. Duration: ${f.duration || "60 minutes"}. Objectives: ${f.objectives || "comprehensive understanding"}. Include warm-up, instruction, activities, assessment, differentiation strategies, materials needed, and homework/extension tasks.`,
  },
  {
    id: "budget-report",
    label: "Budget Report",
    category: "Financial",
    description: "Financial budget analysis report",
    fields: [
      { key: "company", label: "Company/Department", type: "text", placeholder: "Company or department name", required: true },
      { key: "period", label: "Reporting Period", type: "select", options: ["Monthly", "Quarterly", "Annual", "Year-to-Date"] },
      { key: "total_budget", label: "Total Budget", type: "text", placeholder: "$500,000" },
      { key: "categories", label: "Budget Categories", type: "textarea", placeholder: "Personnel, Marketing, Operations, R&D" },
    ],
    buildPrompt: (f) => `Create a ${f.period || "quarterly"} budget report for ${f.company}. Total budget: ${f.total_budget || "as allocated"}. Categories: ${f.categories || "all departments"}. Include executive summary, budget vs actual comparison, variance analysis, spending trends, recommendations, and forecast for next period.`,
  },
  {
    id: "loan-application",
    label: "Loan Application Letter",
    category: "Financial",
    description: "Formal loan request letter",
    fields: [
      { key: "applicant", label: "Applicant Name", type: "text", placeholder: "Your name or company", required: true },
      { key: "bank", label: "Bank/Lender", type: "text", placeholder: "Bank name", required: true },
      { key: "amount", label: "Loan Amount", type: "text", placeholder: "$100,000" },
      { key: "purpose", label: "Loan Purpose", type: "textarea", placeholder: "Business expansion, equipment purchase" },
      { key: "repayment", label: "Proposed Repayment", type: "text", placeholder: "5 years, monthly installments" },
    ],
    buildPrompt: (f) => `Write a formal loan application letter from ${f.applicant} to ${f.bank}. Amount: ${f.amount || "requested amount"}. Purpose: ${f.purpose || "business purposes"}. Repayment: ${f.repayment || "standard terms"}. Include proper formal letter format, financial background summary, collateral mention, signature placement area, and supporting document list.`,
  },
  {
    id: "financial-projection",
    label: "Financial Projection Summary",
    category: "Financial",
    description: "3-year financial forecast",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Your company", required: true },
      { key: "current_revenue", label: "Current Annual Revenue", type: "text", placeholder: "$500,000" },
      { key: "growth_rate", label: "Expected Growth Rate", type: "text", placeholder: "30% YoY" },
      { key: "model", label: "Revenue Model", type: "text", placeholder: "Subscription, one-time, marketplace" },
    ],
    buildPrompt: (f) => `Create a 3-year financial projection summary for ${f.company}. Current revenue: ${f.current_revenue || "starting point"}. Growth rate: ${f.growth_rate || "projected growth"}. Model: ${f.model || "recurring revenue"}. Include revenue projections, cost structure, P&L forecast, cash flow analysis, break-even analysis, and key assumptions.`,
  },
  {
    id: "budget-plan",
    label: "Budget Plan",
    category: "Financial",
    description: "Annual budget with allocations",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Company name", required: true },
      { key: "year", label: "Budget Year", type: "text", placeholder: "2027" },
      { key: "total", label: "Total Budget", type: "text", placeholder: "$1M, $5M" },
      { key: "departments", label: "Departments", type: "textarea", placeholder: "Engineering, Marketing, Sales, Operations" },
    ],
    buildPrompt: (f) => `Create a detailed ${f.year || "annual"} budget plan for ${f.company}. Total: ${f.total || "to be allocated"}. Departments: ${f.departments || "all departments"}. Include executive summary, department allocations, quarterly breakdown, contingency reserves, ROI projections, and approval workflow.`,
  },
  {
    id: "doctors-note",
    label: "Doctor's Note/Medical Certificate",
    category: "Medical",
    description: "Medical certificate for absence",
    fields: [
      { key: "patient", label: "Patient Name", type: "text", placeholder: "Patient full name", required: true },
      { key: "doctor", label: "Doctor Name & Specialty", type: "text", placeholder: "Dr. Smith, General Practitioner" },
      { key: "clinic", label: "Clinic/Hospital", type: "text", placeholder: "City Medical Center" },
      { key: "dates", label: "Absence Dates", type: "text", placeholder: "June 20-23, 2026" },
      { key: "condition", label: "General Condition", type: "select", options: ["General illness", "Flu/Cold", "Surgery recovery", "Medical procedure", "Chronic condition follow-up"] },
    ],
    buildPrompt: (f) => `Create a medical certificate/doctor's note for ${f.patient} from ${f.doctor || "the attending physician"} at ${f.clinic || "the medical facility"}. Dates: ${f.dates || "as specified"}. Condition: ${f.condition || "medical condition"}. Include clinic letterhead, patient details, medical recommendation, return-to-work date, doctor signature area, medical stamp area, and license number placeholder.`,
  },
  {
    id: "prescription",
    label: "Doctor's Prescription (handwritten style)",
    category: "Medical",
    description: "Medical prescription format",
    fields: [
      { key: "patient", label: "Patient Name", type: "text", placeholder: "Patient name", required: true },
      { key: "doctor", label: "Doctor Name", type: "text", placeholder: "Dr. Ahmed Khan" },
      { key: "clinic", label: "Clinic Name", type: "text", placeholder: "City Health Clinic" },
      { key: "medications", label: "Medications", type: "textarea", placeholder: "Amoxicillin 500mg, Paracetamol 650mg" },
      { key: "diagnosis", label: "Diagnosis", type: "text", placeholder: "Upper respiratory infection" },
    ],
    buildPrompt: (f) => `Create a medical prescription in handwritten-style format for ${f.patient} from ${f.doctor || "the physician"} at ${f.clinic || "the clinic"}. Medications: ${f.medications || "as prescribed"}. Diagnosis: ${f.diagnosis || "clinical findings"}. Include Rx header, patient details, date, medication list with dosage and frequency, special instructions, doctor signature area, registration number, and pharmacy stamp area. Format to resemble a traditional prescription pad.`,
  },
  {
    id: "lease-agreement",
    label: "Lease Agreement",
    category: "Real Estate",
    description: "Residential/commercial lease contract",
    fields: [
      { key: "landlord", label: "Landlord/Owner", type: "text", placeholder: "Property owner name", required: true },
      { key: "tenant", label: "Tenant", type: "text", placeholder: "Tenant name", required: true },
      { key: "property", label: "Property Address", type: "textarea", placeholder: "Full property address and description" },
      { key: "rent", label: "Monthly Rent", type: "text", placeholder: "$2,000/month" },
      { key: "duration", label: "Lease Duration", type: "select", options: ["6 months", "1 year", "2 years", "3 years", "5 years"] },
    ],
    buildPrompt: (f) => `Draft a comprehensive lease agreement between ${f.landlord} (Landlord) and ${f.tenant} (Tenant). Property: ${f.property || "as described"}. Rent: ${f.rent || "monthly amount"}. Duration: ${f.duration || "1 year"}. Include property description, rent and deposit terms, maintenance responsibilities, rules, termination clauses, signature blocks for both parties, witness areas, and notary stamp area.`,
  },
  {
    id: "rental-application",
    label: "Rental Application",
    category: "Real Estate",
    description: "Tenant rental application form",
    fields: [
      { key: "applicant", label: "Applicant Name", type: "text", placeholder: "Your full name", required: true },
      { key: "property", label: "Property Applying For", type: "text", placeholder: "Property address or listing" },
      { key: "employment", label: "Employment Details", type: "textarea", placeholder: "Employer, position, income" },
      { key: "references", label: "References", type: "textarea", placeholder: "Previous landlord, employer reference" },
    ],
    buildPrompt: (f) => `Create a rental application form/letter for ${f.applicant} applying for ${f.property || "the property"}. Employment: ${f.employment || "employed professional"}. References: ${f.references || "available upon request"}. Include personal information section, employment verification, rental history, references, income declaration, consent for background check, and applicant signature area.`,
  },
  {
    id: "character-reference",
    label: "Character Reference",
    category: "Personal",
    description: "Personal character reference letter",
    fields: [
      { key: "subject", label: "Person Being Referenced", type: "text", placeholder: "Person's full name", required: true },
      { key: "author", label: "Your Name & Relationship", type: "text", placeholder: "Jane Doe, neighbor for 10 years" },
      { key: "purpose", label: "Purpose", type: "select", options: ["Court proceeding", "Job application", "Rental application", "Immigration", "Adoption", "General"] },
      { key: "qualities", label: "Character Qualities", type: "textarea", placeholder: "Honest, reliable, community-oriented" },
    ],
    buildPrompt: (f) => `Write a character reference letter for ${f.subject} from ${f.author || "the author"}. Purpose: ${f.purpose || "general reference"}. Qualities: ${f.qualities || "excellent character"}. Include formal format, specific examples, declaration of truthfulness, signature placement area, and notary/witness area if applicable.`,
  },
  {
    id: "affidavit",
    label: "Affidavit",
    category: "Personal",
    description: "Sworn statement document",
    fields: [
      { key: "deponent", label: "Deponent (Your Name)", type: "text", placeholder: "Person making the statement", required: true },
      { key: "purpose", label: "Purpose of Affidavit", type: "text", placeholder: "Name change, address proof, income declaration", required: true },
      { key: "facts", label: "Facts to State", type: "textarea", placeholder: "The specific facts being declared under oath" },
      { key: "jurisdiction", label: "Jurisdiction", type: "text", placeholder: "State/Country" },
    ],
    buildPrompt: (f) => `Draft a legal affidavit by ${f.deponent} for the purpose of ${f.purpose}. Facts: ${f.facts || "to be stated"}. Jurisdiction: ${f.jurisdiction || "applicable jurisdiction"}. Include proper affidavit format with numbered paragraphs, oath declaration, deponent signature area, notary public section with stamp area, and date/place fields.`,
  },
  {
    id: "wedding-invitation",
    label: "Wedding/Event Invitation",
    category: "Personal",
    description: "Formal event invitation text",
    fields: [
      { key: "event_type", label: "Event Type", type: "select", options: ["Wedding", "Engagement", "Birthday", "Anniversary", "Corporate Event", "Graduation"], required: true },
      { key: "hosts", label: "Hosts/Couple Names", type: "text", placeholder: "John & Jane Smith", required: true },
      { key: "date", label: "Event Date & Time", type: "text", placeholder: "Saturday, July 15, 2026 at 5:00 PM" },
      { key: "venue", label: "Venue", type: "text", placeholder: "The Grand Ballroom, City Hotel" },
      { key: "style", label: "Style/Theme", type: "select", options: ["Classic/Traditional", "Modern/Minimalist", "Romantic", "Rustic", "Formal/Black Tie"] },
    ],
    buildPrompt: (f) => `Create a beautiful ${f.event_type || "wedding"} invitation for ${f.hosts}. Date: ${f.date || "to be announced"}. Venue: ${f.venue || "venue details"}. Style: ${f.style || "elegant"}. Include main invitation text, RSVP details, dress code, and any special instructions. Format with elegant typography suggestions.`,
  },
  {
    id: "power-of-attorney",
    label: "Power of Attorney",
    category: "Personal",
    description: "Legal authorization document",
    fields: [
      { key: "principal", label: "Principal (Grantor)", type: "text", placeholder: "Person granting authority", required: true },
      { key: "agent", label: "Agent (Attorney-in-Fact)", type: "text", placeholder: "Person receiving authority", required: true },
      { key: "scope", label: "Scope of Authority", type: "select", options: ["General (all matters)", "Limited/Specific", "Financial only", "Healthcare", "Real Estate", "Legal proceedings"] },
      { key: "duration", label: "Duration", type: "select", options: ["Until revoked", "Specific date", "Upon incapacity (Durable)", "For specific transaction"] },
      { key: "jurisdiction", label: "Jurisdiction", type: "text", placeholder: "State/Country" },
    ],
    buildPrompt: (f) => `Draft a Power of Attorney document from ${f.principal} (Principal) granting authority to ${f.agent} (Agent). Scope: ${f.scope || "limited"}. Duration: ${f.duration || "until revoked"}. Jurisdiction: ${f.jurisdiction || "applicable law"}. Include proper legal format, powers granted, limitations, revocation terms, principal signature area, agent acceptance, witness signatures, and notary stamp area.`,
  },
  {
    id: "last-will",
    label: "Last Will & Testament",
    category: "Personal",
    description: "Estate planning will template",
    fields: [
      { key: "testator", label: "Testator (Your Name)", type: "text", placeholder: "Your full legal name", required: true },
      { key: "executor", label: "Executor", type: "text", placeholder: "Person managing the estate" },
      { key: "beneficiaries", label: "Beneficiaries", type: "textarea", placeholder: "Spouse, children, other beneficiaries" },
      { key: "assets", label: "Major Assets", type: "textarea", placeholder: "Property, investments, personal items" },
      { key: "jurisdiction", label: "Jurisdiction", type: "text", placeholder: "State/Country" },
    ],
    buildPrompt: (f) => `Draft a Last Will and Testament for ${f.testator}. Executor: ${f.executor || "to be named"}. Beneficiaries: ${f.beneficiaries || "family members"}. Assets: ${f.assets || "estate assets"}. Jurisdiction: ${f.jurisdiction || "applicable law"}. Include proper legal format, revocation of prior wills, appointment of executor, specific bequests, residuary clause, guardianship (if applicable), testator signature area, witness signatures (2-3), and notary area. Note: template for review by legal counsel.`,
  },
  {
    id: "client-proposal",
    label: "Client Proposal",
    category: "Operations",
    description: "Professional proposal with scope and pricing",
    fields: [
      { key: "your_company", label: "Your Company", type: "text", placeholder: "Your company name", required: true },
      { key: "client", label: "Client Name", type: "text", placeholder: "Client company", required: true },
      { key: "project", label: "Project/Service", type: "text", placeholder: "Website redesign, app development", required: true },
      { key: "budget", label: "Budget Range", type: "text", placeholder: "$10,000 - $25,000" },
      { key: "timeline", label: "Timeline", type: "text", placeholder: "8 weeks, 3 months" },
    ],
    buildPrompt: (f) => `Write a professional client proposal from ${f.your_company} to ${f.client} for ${f.project}. Budget: ${f.budget || "to be discussed"}. Timeline: ${f.timeline || "to be determined"}. Include executive summary, proposed solution, scope, deliverables, timeline, pricing, terms, and signature areas.`,
  },
  {
    id: "meeting-agenda",
    label: "Meeting Agenda",
    category: "Operations",
    description: "Structured agenda with action items",
    fields: [
      { key: "meeting", label: "Meeting Type", type: "select", options: ["Board Meeting", "Team Standup", "Client Kickoff", "Quarterly Review", "Strategy Session", "1:1 Review"], required: true },
      { key: "duration", label: "Duration", type: "select", options: ["30 minutes", "1 hour", "2 hours", "Half day"] },
      { key: "attendees", label: "Key Attendees", type: "text", placeholder: "Team leads, stakeholders" },
      { key: "topics", label: "Key Topics", type: "textarea", placeholder: "What needs to be discussed?" },
    ],
    buildPrompt: (f) => `Create a structured ${f.meeting} agenda. Duration: ${f.duration || "1 hour"}. Attendees: ${f.attendees || "team members"}. Topics: ${f.topics || "standard agenda"}. Include objectives, timed discussion points, decision items, action item slots, and follow-up section.`,
  },
  {
    id: "sop-document",
    label: "SOP Document",
    category: "Operations",
    description: "Standard Operating Procedure",
    fields: [
      { key: "process", label: "Process Name", type: "text", placeholder: "Customer Onboarding, Order Fulfillment", required: true },
      { key: "department", label: "Department", type: "text", placeholder: "Sales, Operations, Support" },
      { key: "tools", label: "Tools/Systems", type: "text", placeholder: "Salesforce, Slack, Notion" },
      { key: "owner", label: "Process Owner", type: "text", placeholder: "Operations Manager" },
    ],
    buildPrompt: (f) => `Create a Standard Operating Procedure for ${f.process} in ${f.department || "operations"}. Tools: ${f.tools || "standard tools"}. Owner: ${f.owner || "department head"}. Include purpose, scope, roles, step-by-step instructions, quality checks, exceptions, and revision history template.`,
  },
  {
    id: "job-description",
    label: "Job Description",
    category: "Operations",
    description: "Compelling JD with requirements",
    fields: [
      { key: "title", label: "Job Title", type: "text", placeholder: "Senior Product Manager", required: true },
      { key: "company", label: "Company", type: "text", placeholder: "Your company name" },
      { key: "level", label: "Level", type: "select", options: ["Entry Level", "Mid Level", "Senior", "Lead", "Manager", "Director", "VP", "C-Level"] },
      { key: "remote", label: "Work Mode", type: "select", options: ["Remote", "Hybrid", "On-site"] },
      { key: "salary_range", label: "Salary Range (optional)", type: "text", placeholder: "$120K - $160K" },
    ],
    buildPrompt: (f) => `Write a compelling job description for ${f.level || ""} ${f.title} at ${f.company || "our company"}. Work mode: ${f.remote || "flexible"}. Salary: ${f.salary_range || "competitive"}. Include engaging intro, responsibilities, requirements (must-have vs nice-to-have), benefits, culture, and application instructions.`,
  },
  {
    id: "contract-template",
    label: "Contract Template",
    category: "Operations",
    description: "Professional contract with terms",
    fields: [
      { key: "type", label: "Contract Type", type: "select", options: ["Service Agreement", "NDA", "Employment", "Partnership", "Consulting", "Freelancer"], required: true },
      { key: "parties", label: "Parties Involved", type: "text", placeholder: "Company A and Contractor B" },
      { key: "jurisdiction", label: "Jurisdiction", type: "text", placeholder: "Delaware, USA" },
      { key: "value", label: "Contract Value", type: "text", placeholder: "$50,000" },
    ],
    buildPrompt: (f) => `Draft a ${f.type} contract between ${f.parties || "the parties"}. Jurisdiction: ${f.jurisdiction || "to be specified"}. Value: ${f.value || "as agreed"}. Include definitions, scope, payment, confidentiality, IP, termination, disputes, and signature blocks. Note: template for legal review.`,
  },
  {
    id: "performance-review",
    label: "Performance Review",
    category: "Operations",
    description: "Employee performance evaluation",
    fields: [
      { key: "employee", label: "Employee Name", type: "text", placeholder: "Employee name", required: true },
      { key: "position", label: "Position", type: "text", placeholder: "Software Engineer" },
      { key: "period", label: "Review Period", type: "text", placeholder: "Q1 2026, Annual 2025" },
      { key: "achievements", label: "Key Achievements", type: "textarea", placeholder: "Delivered project X, mentored 3 juniors" },
      { key: "areas_improvement", label: "Areas for Improvement", type: "textarea", placeholder: "Communication, time management" },
    ],
    buildPrompt: (f) => `Create a performance review for ${f.employee}, ${f.position || "team member"}. Period: ${f.period || "recent quarter"}. Achievements: ${f.achievements || "notable contributions"}. Areas for improvement: ${f.areas_improvement || "growth opportunities"}. Include overall rating section, goal assessment, competency evaluation, development plan, and signature areas for employee and manager.`,
  },
  {
    id: "termination-letter",
    label: "Termination Letter",
    category: "Operations",
    description: "Employee termination notice",
    fields: [
      { key: "company", label: "Company Name", type: "text", placeholder: "Company name", required: true },
      { key: "employee", label: "Employee Name", type: "text", placeholder: "Employee name", required: true },
      { key: "position", label: "Position", type: "text", placeholder: "Their role" },
      { key: "reason", label: "Reason", type: "select", options: ["Performance", "Restructuring", "End of contract", "Misconduct", "Redundancy"] },
      { key: "effective_date", label: "Effective Date", type: "text", placeholder: "July 31, 2026" },
    ],
    buildPrompt: (f) => `Write a professional termination letter from ${f.company} to ${f.employee}, ${f.position || "employee"}. Reason: ${f.reason || "organizational reasons"}. Effective: ${f.effective_date || "with notice period"}. Include formal tone, clear reasoning, final pay details, benefits information, return of property section, and authorized signature area.`,
  },
  {
    id: "bank-statement-request",
    label: "Bank Statement Request Letter",
    category: "Operations",
    description: "Formal request for bank statements",
    fields: [
      { key: "account_holder", label: "Account Holder Name", type: "text", placeholder: "Your name or company", required: true },
      { key: "bank", label: "Bank Name", type: "text", placeholder: "Bank name", required: true },
      { key: "account_type", label: "Account Type", type: "select", options: ["Savings", "Current/Checking", "Business", "Joint"] },
      { key: "period", label: "Statement Period", type: "text", placeholder: "January - June 2026" },
      { key: "purpose", label: "Purpose", type: "text", placeholder: "Loan application, visa, audit" },
    ],
    buildPrompt: (f) => `Write a formal bank statement request letter from ${f.account_holder} to ${f.bank}. Account type: ${f.account_type || "bank account"}. Period: ${f.period || "recent months"}. Purpose: ${f.purpose || "official purposes"}. Include formal letter format, account details placeholder, specific request, preferred delivery method, and signature placement area.`,
  },
];

function CanvasDocument({ doc, onBack, onDelete }) {
  const contentRef = useRef(null);

  const handleExportPDF = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.warn("Please allow popups to export PDF");
      return;
    }
    printWindow.document.write(`
      <html><head><title>${doc.title || "Document"}</title>
      <style>
        @page { margin: 0; size: A4; }
        @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
        * { box-sizing: border-box; }
        body { font-family: system-ui, -apple-system, sans-serif; padding: 48px; line-height: 1.75; color: #1f2937; background: #ffffff; max-width: 900px; margin: 0 auto; }
        h1 { font-size: 28px; font-weight: 800; color: #111827; margin: 32px 0 16px; border-bottom: 2px solid #06b6d4; padding-bottom: 12px; }
        h2 { font-size: 20px; font-weight: 700; color: #111827; margin: 28px 0 14px; }
        h3 { font-size: 16px; font-weight: 600; color: #374151; margin: 20px 0 10px; }
        p { color: #4b5563; font-size: 14px; margin-bottom: 14px; }
        ul, ol { color: #4b5563; font-size: 14px; padding-left: 24px; margin-bottom: 16px; }
        li { margin-bottom: 8px; }
        strong, b { color: #111827; font-weight: 600; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th { background: #f3f4f6; border: 1px solid #e5e7eb; padding: 10px 14px; font-size: 12px; font-weight: 700; text-align: left; }
        td { border: 1px solid #e5e7eb; padding: 10px 14px; font-size: 13px; }
        blockquote { border-left: 3px solid #06b6d4; padding: 12px 16px; margin: 16px 0; background: #f0fdfa; }
        .footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 11px; color: #9ca3af; }
      </style></head>
      <body>${contentRef.current?.innerHTML || doc.content}<div class="footer">Generated by Kronos AI - Doc Studio</div></body></html>
    `);
    printWindow.document.close();
    setTimeout(() => { try { printWindow.print(); } catch (e) { /* silent */ } }, 500);
  };

  const handleExportImage = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.warn("Please allow popups to export as image");
      return;
    }
    printWindow.document.write(`
      <html><head><title>${doc.title || "Document"}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: system-ui, sans-serif; padding: 48px; line-height: 1.75; color: #e5e7eb; background: linear-gradient(135deg, #0a0a0f 0%, #111827 100%); min-height: 100vh; max-width: 900px; margin: 0 auto; }
        h1 { font-size: 28px; font-weight: 800; color: #ffffff; border-bottom: 2px solid rgba(6,182,212,0.4); padding-bottom: 12px; }
        h2 { font-size: 20px; font-weight: 700; color: #ffffff; }
        h3 { font-size: 16px; font-weight: 600; color: #d1d5db; }
        p { color: #9ca3af; font-size: 14px; }
        ul, ol { color: #9ca3af; }
        strong { color: #ffffff; }
        table { width: 100%; border-collapse: collapse; margin: 16px 0; }
        th { background: rgba(6,182,212,0.2); border: 1px solid rgba(255,255,255,0.1); padding: 10px; font-size: 12px; color: #06b6d4; }
        td { border: 1px solid rgba(255,255,255,0.08); padding: 10px; font-size: 13px; color: #d1d5db; }
      </style>
      <script>window.onload=function(){document.body.style.cursor='crosshair';alert('Right-click the page and select Save As Image, or use your browser screenshot tool (Ctrl+Shift+S in Firefox/Edge)');}</script>
      </head><body>${contentRef.current?.innerHTML || doc.content}</body></html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-gray-400 hover:text-cyan-400 transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Back to Doc Studio
        </button>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleExportPDF} className="px-3 py-1.5 rounded-xl text-xs text-gray-300 border border-white/10 hover:border-cyan-500/30 transition-all">
            Export PDF
          </button>
          <button onClick={handleExportImage} className="px-3 py-1.5 rounded-xl text-xs text-gray-300 border border-white/10 hover:border-cyan-500/30 transition-all">
            Export Image
          </button>
          <button onClick={onDelete} className="px-3 py-1.5 rounded-xl text-xs text-red-400 border border-red-500/20 hover:border-red-500/40 transition-all">
            Delete
          </button>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden border border-white/10" style={{ background: "#0a0a0f" }}>
        <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div>
            <h2 className="text-white font-bold text-lg">{doc.title}</h2>
            <p className="text-gray-500 text-xs mt-1">Generated with Claude Sonnet 4.6 + Seedream 4.0</p>
          </div>
        </div>
        <div
          ref={contentRef}
          className="px-6 py-6 prose prose-invert prose-sm max-w-none
            [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-white [&_h1]:mt-6 [&_h1]:mb-3
            [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-5 [&_h2]:mb-2
            [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-200 [&_h3]:mt-4 [&_h3]:mb-2
            [&_p]:text-gray-300 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-3
            [&_ul]:text-gray-300 [&_ul]:text-sm [&_ul]:space-y-1
            [&_ol]:text-gray-300 [&_ol]:text-sm [&_ol]:space-y-1
            [&_strong]:text-white
            [&_table]:w-full [&_table]:border-collapse [&_table]:my-4
            [&_th]:bg-white/5 [&_th]:border [&_th]:border-white/10 [&_th]:px-3 [&_th]:py-2 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-white
            [&_td]:border [&_td]:border-white/10 [&_td]:px-3 [&_td]:py-2 [&_td]:text-xs [&_td]:text-gray-300
            [&_blockquote]:border-l-2 [&_blockquote]:border-cyan-500/50 [&_blockquote]:pl-4 [&_blockquote]:text-gray-400
            [&_code]:bg-white/5 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-cyan-400 [&_code]:text-xs"
          dangerouslySetInnerHTML={{ __html: doc.content }}
        />
      </div>
    </div>
  );
}

export default function CanvasTab() {
  const [documents, setDocuments] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [fieldValues, setFieldValues] = useState({});
  const [scraping, setScraping] = useState(false);
  const [scrapingMsg, setScrapingMsg] = useState("");
  const [additionalPrompt, setAdditionalPrompt] = useState("");
  const [signatureFile, setSignatureFile] = useState(null);
  const [referenceFile, setReferenceFile] = useState(null);
  const [analyzingRef, setAnalyzingRef] = useState(false);
  const [refAnalysis, setRefAnalysis] = useState("");
  const [signatureMode, setSignatureMode] = useState("upload"); // "upload" | "ai"
  const [aiSignatureName, setAiSignatureName] = useState("");

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const { data } = await api.get("/canvas/documents");
      setDocuments(data.documents || []);
    } catch (e) {}
  };

  const handleGenerate = async () => {
    const tpl = TEMPLATES.find((t) => t.id === selectedTemplate);
    if (!tpl) {
      toast.warn("Select a template first");
      return;
    }
    const finalPrompt = tpl.buildPrompt(fieldValues) + (additionalPrompt ? `\n\nAdditional instructions: ${additionalPrompt}` : "");
    if (!finalPrompt.trim()) return;

    let promptWithExtras = finalPrompt;

    // Add reference analysis context
    if (refAnalysis) {
      promptWithExtras += `\n\nREFERENCE DOCUMENT STYLE (match this exactly):\n${refAnalysis}`;
    }

    // Add signature instructions
    if (signatureMode === "ai" && aiSignatureName) {
      promptWithExtras += `\n\nSIGNATURE: Generate a realistic handwritten-style signature for "${aiSignatureName}" and place it in the signature area of the document.`;
    } else if (signatureMode === "upload" && signatureFile) {
      promptWithExtras += "\n\nSIGNATURE: User has uploaded a signature image — include a signature placeholder area marked [SIGNATURE_HERE] where it will be embedded.";
    }

    setGenerating(true);
    setError("");

    try {
      // Use FormData if we have files to upload
      const formData = new FormData();
      formData.append("prompt", promptWithExtras.trim());
      formData.append("template", selectedTemplate);
      if (signatureMode === "upload" && signatureFile) {
        formData.append("signature", signatureFile);
      }
      if (signatureMode === "ai" && aiSignatureName) {
        formData.append("ai_signature_name", aiSignatureName);
      }
      if (referenceFile) {
        formData.append("reference", referenceFile);
      }

      const { data } = await api.post("/canvas/generate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });

      if (data.error) {
        setError(data.error);
      } else {
        setActiveDoc(data);
        setSelectedTemplate(null);
        setFieldValues({});
        setAdditionalPrompt("");
        setSignatureFile(null);
        setReferenceFile(null);
        setRefAnalysis("");
        setAiSignatureName("");
        loadDocuments();
        toast.success("Document generated successfully");
      }
    } catch (e) {
      setError(e.response?.data?.error || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/canvas/documents/${docId}`);
      setDocuments(documents.filter((d) => d.id !== docId));
      if (activeDoc?.id === docId) setActiveDoc(null);
      toast.success("Document deleted");
    } catch (e) {}
  };

  const handleScrapeWebsite = async (url) => {
    if (!url || !url.startsWith("http")) {
      toast.warn("Enter a valid URL starting with http");
      return;
    }
    setScraping(true);
    setScrapingMsg("");
    try {
      const { data } = await api.post("/scrape-website", { url });
      const info = data.business_info || data;
      if (info && (info.name || info.description || info.business_type)) {
        const updates = {};
        const found = [];
        if (info.name) { updates.company = info.name; updates.your_company = info.name; updates.from_company = info.name; found.push(`Company: ${info.name}`); }
        if (info.business_type) { updates.industry = info.business_type; found.push(`Industry: ${info.business_type}`); }
        if (info.description || info.about) { updates.description = (info.description || info.about).slice(0, 300); found.push("Description found"); }
        if (info.services?.length) { updates.services = info.services.join(", "); found.push(`${info.services.length} services`); }
        setFieldValues((prev) => ({ ...prev, ...updates }));
        setScrapingMsg(found.length > 0 ? `Analyzed: ${found.join(" | ")}` : "No data extracted");
      } else {
        setScrapingMsg("Could not extract info. Try a different URL.");
      }
    } catch (e) {
      setScrapingMsg("Failed to analyze website.");
    } finally {
      setScraping(false);
    }
  };

  const handleAnalyzeReference = async () => {
    if (!referenceFile) {
      toast.warn("Upload a reference image or PDF first");
      return;
    }
    setAnalyzingRef(true);
    setRefAnalysis("");
    try {
      const formData = new FormData();
      formData.append("file", referenceFile);
      const { data } = await api.post("/canvas/analyze-reference", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      if (data.analysis) {
        setRefAnalysis(data.analysis);
        setAdditionalPrompt((prev) => {
          const prefix = prev ? prev + "\n\n" : "";
          return prefix + "REFERENCE FORMAT:\n" + data.analysis;
        });
        toast.success("Reference analyzed — style extracted and applied to prompt");
      } else {
        toast.error("Could not analyze reference");
      }
    } catch (e) {
      toast.error("Reference analysis failed");
    } finally {
      setAnalyzingRef(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const selectedTemplateObj = TEMPLATES.find((t) => t.id === selectedTemplate);

  const filteredTemplates = selectedCategory === "All" ? TEMPLATES : TEMPLATES.filter((t) => t.category === selectedCategory);

  if (activeDoc) {
    return (
      <div className="space-y-6">
        <CanvasDocument
          doc={activeDoc}
          onBack={() => setActiveDoc(null)}
          onDelete={() => { handleDelete(activeDoc.id); setActiveDoc(null); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl p-8 text-center" style={{ background: "#0a0a0f", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
        <h1 className="text-3xl font-black text-white mb-2">Doc Studio</h1>
        <p className="text-gray-400 text-sm max-w-xl mx-auto">
          Generate professional documents, certificates, and legal templates. Claude Sonnet creates content with proper formatting, Seedream generates visual elements. Export as PDF or Image.
        </p>
        <div className="flex justify-center mt-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.2)" }}>
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
            <span className="text-[11px] font-bold text-cyan-400 tracking-wide">Powered by Claude Sonnet 4.6 + Seedream 4.0</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ background: "#0a0a0f", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-white font-bold text-sm">Create New Document</h3>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  selectedCategory === cat ? "text-white" : "text-gray-500 hover:text-gray-300"
                }`}
                style={{
                  background: selectedCategory === cat ? `${CATEGORY_COLORS[cat] || "rgba(6,182,212,0.15)"}20` : "rgba(255,255,255,0.02)",
                  border: selectedCategory === cat ? `1px solid ${CATEGORY_COLORS[cat] || "#06b6d4"}50` : "1px solid rgba(255,255,255,0.06)",
                  color: selectedCategory === cat ? (CATEGORY_COLORS[cat] || "#06b6d4") : undefined,
                }}
              >
                {CATEGORY_ICONS[cat]}
                <span>{cat}</span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {filteredTemplates.map((t) => {
              const color = CATEGORY_COLORS[t.category] || "#06b6d4";
              const isSelected = selectedTemplate === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => { setSelectedTemplate(t.id); setFieldValues({}); setScrapingMsg(""); }}
                  className={`text-left p-3 rounded-xl transition-all duration-200 ${isSelected ? "scale-[1.02]" : "hover:scale-[1.01]"}`}
                  style={{
                    background: isSelected ? `${color}10` : "#0a0a0f",
                    border: isSelected ? `1px solid ${color}60` : "1px solid rgba(255,255,255,0.08)",
                    borderLeft: `3px solid ${isSelected ? color : `${color}40`}`,
                  }}
                >
                  <p className={`text-sm font-semibold ${isSelected ? "text-white" : "text-gray-200"}`}>{t.label}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">{t.description}</p>
                </button>
              );
            })}
          </div>

          {selectedTemplateObj && (
            <div className="mt-4 p-4 rounded-xl space-y-4" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-white font-semibold text-sm">{selectedTemplateObj.label}</h4>
                  <p className="text-gray-500 text-xs mt-0.5">{selectedTemplateObj.description}</p>
                </div>
                <button onClick={() => { setSelectedTemplate(null); setFieldValues({}); }} className="text-gray-500 hover:text-white p-1 transition-colors">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {selectedTemplateObj.fields.map((field) => (
                  <div key={field.key} className={field.type === "textarea" ? "sm:col-span-2" : ""}>
                    <label className="block text-xs text-gray-400 mb-1 font-medium">
                      {field.label}
                      {field.required && <span className="text-cyan-400 ml-0.5">*</span>}
                    </label>
                    {field.type === "textarea" ? (
                      <textarea
                        value={fieldValues[field.key] || ""}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full h-20 px-3 py-2 rounded-xl text-white text-sm placeholder:text-gray-600 resize-none focus:outline-none border border-white/10 focus:border-cyan-500/40 transition-colors"
                        style={{ background: "rgba(0,0,0,0.5)" }}
                      />
                    ) : field.type === "select" ? (
                      <select
                        value={fieldValues[field.key] || ""}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        className="w-full px-3 py-2 rounded-xl text-white text-sm focus:outline-none border border-white/10 focus:border-cyan-500/40 transition-colors"
                        style={{ background: "rgba(0,0,0,0.5)" }}
                      >
                        <option value="">Select...</option>
                        {field.options?.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : field.type === "url" ? (
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={fieldValues[field.key] || ""}
                          onChange={(e) => handleFieldChange(field.key, e.target.value)}
                          placeholder={field.placeholder}
                          className="flex-1 px-3 py-2 rounded-xl text-white text-sm placeholder:text-gray-600 focus:outline-none border border-white/10 focus:border-cyan-500/40 transition-colors"
                          style={{ background: "rgba(0,0,0,0.5)" }}
                        />
                        <button
                          onClick={() => handleScrapeWebsite(fieldValues[field.key])}
                          disabled={scraping || !fieldValues[field.key]}
                          className="px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-40 transition-all flex items-center gap-1.5 text-cyan-400 border border-cyan-500/30 hover:border-cyan-500/60"
                          style={{ background: "rgba(6,182,212,0.08)" }}
                        >
                          {scraping ? (
                            <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20" /></svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                          )}
                          Analyze
                        </button>
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={fieldValues[field.key] || ""}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full px-3 py-2 rounded-xl text-white text-sm placeholder:text-gray-600 focus:outline-none border border-white/10 focus:border-cyan-500/40 transition-colors"
                        style={{ background: "rgba(0,0,0,0.5)" }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {scrapingMsg && (
                <div className="px-3 py-2 rounded-xl text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                  {scrapingMsg}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="block text-xs text-gray-400 mb-1 font-medium">Company Website URL</label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={fieldValues._website_url || ""}
                      onChange={(e) => handleFieldChange("_website_url", e.target.value)}
                      placeholder="https://yourcompany.com"
                      className="flex-1 px-3 py-2 rounded-xl text-white text-sm placeholder:text-gray-600 focus:outline-none border border-white/10 focus:border-cyan-500/40 transition-colors"
                      style={{ background: "rgba(0,0,0,0.5)" }}
                    />
                    <button
                      onClick={() => handleScrapeWebsite(fieldValues._website_url)}
                      disabled={scraping || !fieldValues._website_url}
                      className="px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-40 transition-all flex items-center gap-1.5 text-cyan-400 border border-cyan-500/30"
                      style={{ background: "rgba(6,182,212,0.08)" }}
                    >
                      {scraping ? (
                        <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20" /></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                      )}
                      Analyze
                    </button>
                  </div>
                </div>

                {/* ── Signature Section ── */}
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Signature</label>
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setSignatureMode("upload")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${signatureMode === "upload" ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40" : "bg-white/5 text-gray-400 border border-white/10"}`}
                    >
                      Upload Image
                    </button>
                    <button
                      type="button"
                      onClick={() => setSignatureMode("ai")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${signatureMode === "ai" ? "bg-purple-500/20 text-purple-400 border border-purple-500/40" : "bg-white/5 text-gray-400 border border-white/10"}`}
                    >
                      AI Generate
                    </button>
                  </div>
                  {signatureMode === "upload" ? (
                    <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm cursor-pointer border border-white/10 hover:border-cyan-500/30 transition-colors" style={{ background: "rgba(0,0,0,0.5)" }}>
                      <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                      <span className={`text-xs ${signatureFile ? "text-cyan-400" : "text-gray-500"}`}>
                        {signatureFile ? signatureFile.name : "Choose signature image..."}
                      </span>
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => setSignatureFile(e.target.files?.[0] || null)} />
                    </label>
                  ) : (
                    <input
                      type="text"
                      value={aiSignatureName}
                      onChange={(e) => setAiSignatureName(e.target.value)}
                      placeholder="Enter name for AI signature (e.g. Dr. Ahmed Khan)"
                      className="w-full px-3 py-2.5 rounded-xl text-white text-sm placeholder:text-gray-600 focus:outline-none border border-purple-500/20 focus:border-purple-500/40 transition-colors"
                      style={{ background: "rgba(0,0,0,0.5)" }}
                    />
                  )}
                </div>

                {/* ── Reference Upload + Analyze ── */}
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Upload Reference (AI will match its style)</label>
                  <div className="flex gap-2">
                    <label className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm cursor-pointer border border-white/10 hover:border-cyan-500/30 transition-colors" style={{ background: "rgba(0,0,0,0.5)" }}>
                      <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                      <span className={`text-xs truncate ${referenceFile ? "text-cyan-400" : "text-gray-500"}`}>
                        {referenceFile ? referenceFile.name : "Choose reference image/PDF..."}
                      </span>
                      <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { setReferenceFile(e.target.files?.[0] || null); setRefAnalysis(""); }} />
                    </label>
                    <button
                      onClick={handleAnalyzeReference}
                      disabled={analyzingRef || !referenceFile}
                      className="px-4 py-2.5 rounded-xl text-xs font-semibold disabled:opacity-40 transition-all flex items-center gap-1.5 text-cyan-400 border border-cyan-500/30 hover:border-cyan-500/60 whitespace-nowrap"
                      style={{ background: "rgba(6,182,212,0.08)" }}
                    >
                      {analyzingRef ? (
                        <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20" /></svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                      )}
                      Analyze
                    </button>
                  </div>
                  {refAnalysis && (
                    <div className="mt-2 px-3 py-2 rounded-xl text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 max-h-24 overflow-y-auto">
                      <span className="font-semibold">Extracted Style:</span> {refAnalysis.slice(0, 200)}{refAnalysis.length > 200 ? "..." : ""}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1 font-medium">Additional Instructions (optional)</label>
                <textarea
                  value={additionalPrompt}
                  onChange={(e) => setAdditionalPrompt(e.target.value)}
                  placeholder="Any additional context or specific formatting requests... (e.g., 'Use blue ink handwriting', 'Add gold border', 'Match the reference document style')"
                  className="w-full h-32 px-3 py-2 rounded-xl text-white text-sm placeholder:text-gray-600 resize-y focus:outline-none border border-white/10 focus:border-cyan-500/40 transition-colors"
                  style={{ background: "rgba(0,0,0,0.5)" }}
                />
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                style={{
                  background: generating ? "rgba(6,182,212,0.15)" : "linear-gradient(135deg, rgba(6,182,212,0.3), rgba(168,85,247,0.2))",
                  border: "1px solid rgba(6,182,212,0.4)",
                }}
              >
                {generating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="50 20" /></svg>
                    Generating Document...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
                    Generate Document
                  </>
                )}
              </button>

              {error && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {documents.length > 0 && (
        <div className="rounded-2xl overflow-hidden" style={{ background: "#0a0a0f", border: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
            <h3 className="text-white font-bold text-sm">Previous Documents</h3>
            <span className="text-xs text-gray-500">{documents.length} document{documents.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between px-5 py-3 hover:bg-white/[0.02] transition-colors"
              >
                <button onClick={() => setActiveDoc(doc)} className="flex-1 text-left">
                  <p className="text-sm text-white font-medium">{doc.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {new Date(doc.created_at).toLocaleDateString()} - {doc.template || "Custom"}
                  </p>
                </button>
                <button
                  onClick={() => handleDelete(doc.id)}
                  className="text-gray-600 hover:text-red-400 transition-colors p-1 ml-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" /></svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
